/**
 * Knowledge Update Banner.
 *
 * Rendered below a chat message when the AI extracted new knowledge
 * from the interviewee's response. Shows a count of updated fields
 * and a button to open the sidebar.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ChevronRight } from 'lucide-react';
import type { KnowledgeDelta } from '@/lib/api/publicInterview';

interface KnowledgeUpdateBannerProps {
  delta: KnowledgeDelta;
  onViewChanges: (delta: KnowledgeDelta) => void;
}

const KnowledgeUpdateBanner: React.FC<KnowledgeUpdateBannerProps> = ({
  delta,
  onViewChanges,
}) => {
  const count = delta.updated_fields.length;
  if (count === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
    >
      <button
        onClick={() => onViewChanges(delta)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/5 border border-primary/15 text-xs text-primary hover:bg-primary/10 transition-colors cursor-pointer"
      >
        <Sparkles className="h-3.5 w-3.5 shrink-0" />
        <span className="flex-1 text-left">
          {count} knowledge field{count !== 1 ? 's' : ''} updated
        </span>
        <span className="text-primary/60 shrink-0">View Changes</span>
        <ChevronRight className="h-3 w-3 text-primary/40 shrink-0" />
      </button>
    </motion.div>
  );
};

export { KnowledgeUpdateBanner };
